import { api, LightningElement, wire } from "lwc";
import { logger } from "@devforce/trailhead-client-logger";
import classNames from "classnames";

import { assignedSlotNames } from "common/slot";
import {
  ResizeObserverAdapter,
  ResizeObserverEntry,
} from "common/resizeObserverAdapter";
import { createTrack, TrackEvent } from "common/track";
import { TruncateType, TruncateDisplayMode } from "tds/truncate";

type TitleMode = "anchor" | "button" | "text";

export type Size = "default" | "small";
export type Variant = "brick" | "brick-tile" | "tile" | "tile-brick";

export type ResizeEvent = { entry: ResizeObserverEntry; size: Size };
export default class extends LightningElement {
  @api description: string | null = null;
  @api descriptionTruncateType: TruncateType | null = null;
  @api descriptionTruncateDisplayMode: TruncateDisplayMode | null = null;
  @api descriptionTruncateLabel: string | null = null;
  @api descriptionHtml: string | null = null;
  @api path: string | null = null;
  @api pathTarget: string | null = null;
  @api pretitle: string | null = null;
  @api pretitleLinkHref: string | null = null;
  @api pretitleLinkTarget: string | null = null;
  @api pretitleLinkTitle: string | null = null;
  @api pretitleTruncateLines: string | number = 1;
  @api pretitleHtml: string | null = null;
  @api title: string = "";
  @api titleHeadingLevel: "1" | "2" | "3" | "4" | null = null;
  @api titleTruncateLines: string | number = 2;
  @api descriptionTruncateLines: string | number = 2;
  @api variant: Variant = "brick";
  @api metaLeftTruncateLines: string | number = 1;
  @api metaRightTruncateLines: string | number = 1;

  @api
  get titleMode(): TitleMode {
    // Backwards compatability
    if (this._titleMode === "anchor" && !this.path) {
      if (process.env.NODE_ENV === "development") {
        logger.warn(
          `title-mode was set to "anchor" but is missing "path" attribute, falling back to "button"`
        );
      }
      return "button";
    }
    return this._titleMode;
  }
  set titleMode(value) {
    this._titleMode = value;
  }
  private _titleMode: TitleMode = "anchor";

  private size: Size = "default";
  private slots = new Set<string>();
  private slotNames = "";
  private width = Number.MAX_SAFE_INTEGER;

  private track = createTrack<"tds-summary">(this);

  private get contentAlignment() {
    return this.isCompact ? "center" : "start";
  }

  private get computedVariant() {
    switch (this.variant) {
      case "brick-tile":
        return this.width < 350 ? "tile" : "brick";
      case "tile-brick":
        return this.width > 500 ? "brick" : "tile";
      default:
        return this.variant;
    }
  }

  private get isCompact() {
    return (
      !this.description &&
      !this.descriptionHtml &&
      !this.slots.has("meta-left") &&
      !this.slots.has("meta-right")
    );
  }

  private get isTile() {
    return this.computedVariant === "tile";
  }

  private get showPretitle() {
    return (
      typeof this.pretitle === "string" ||
      typeof this.pretitleLinkTitle === "string" ||
      typeof this.pretitleHtml === "string" ||
      this.slots.has("pretitle")
    );
  }

  private get titleIsAnchor() {
    return this.titleMode === "anchor";
  }

  private get titleIsButton() {
    return this.titleMode === "button";
  }

  private get titleIsText() {
    return this.titleMode === "text";
  }

  private get titleTruncateLineSize() {
    return 18 * 1.3; // font-size * line-height
  }

  private get metaTruncateLineSize() {
    return 14 * 1.4; // font-size * line-height
  }

  private get showDescription() {
    return this.description || this.descriptionHtml;
  }

  private get descriptionClassName() {
    return classNames("content__description", {
      "content__description--html": !!this.descriptionHtml,
    });
  }

  @wire(ResizeObserverAdapter, { template: "$template" })
  private onResize(entry: ResizeObserverEntry) {
    const breakpoint = this.variant === "tile" ? 280 : 400;
    this.size = entry.contentRect.width < breakpoint ? "small" : "default";
    this.width = entry.contentRect.width;
    this.dispatchEvent(
      new CustomEvent("resize", { detail: { entry, size: this.size } })
    );
  }

  private onSlotChange() {
    let slots = assignedSlotNames(this.template);
    this.slots = new Set(slots);
    this.slotNames = slots.join(" ");
  }

  private onTitleClick() {
    this.dispatchEvent(new CustomEvent("navigate"));
  }

  private handleDescriptionTruncateTrack(e: TrackEvent<"tds-truncate">) {
    this.track("truncate_link_clicked", e.detail.payload);
  }
}
